/**
 * CypAdhdMedicalScreeningPanel.js
 *
 * The edit surface for WLC's treatment contraindication screening
 * (cypAdhdFlow.medicalScreening === 'contraindicationsV1'). Rendered by
 * 1_CypAdhdClinicalInterview.js as the last panel of the Medical History
 * section (nine items) and of the Family History section (one item). The
 * item list comes from cypAdhdMedicalScreeningConfig.js verbatim.
 *
 * One card per group, in the structured risk panel's vocabulary so the two
 * clinician panels read as one module: the same PANEL_CLASS card, the same
 * header row (title left, "No to all?" centred over the Present? column),
 * an Item | Present? | Details table with the same 30/20/50 widths, and the
 * shared YesNoToggle per row. The detail column holds a free-text box that
 * appears only once the row is Yes, with the item's own hint as placeholder
 * (Allergies asks for the nature and severity of past reactions).
 *
 * Data contract (the structured risk panel's): this component owns no
 * state. It receives the current `data` object and calls `onChange` with a
 * complete replacement object on every edit; the page writes that object
 * wholesale to the stage document. Rules mirrored from the risk panel:
 *   - clicking the half that is already lit clears the row back to null
 *     (unanswered), as the adult ADHD Risk Assessment does;
 *   - a No or a clear empties the detail, because the detail belongs to the
 *     Yes answer and a stale note under a No would contradict the record;
 *   - "No to all?" is a single onChange call for the whole group, so a
 *     failed save can never leave the group half-updated; it is disabled
 *     when the group is already all-No (it would do nothing);
 *   - every handler is a no-op when `canEdit` is false, in addition to the
 *     controls being disabled, so a read-only viewer can never write even
 *     if a disabled attribute were lost.
 *
 * Regulatory boundary: this records what the clinician decided and nothing
 * more. There is no computed "contraindicated" flag, no notice and no gate.
 */
import React from 'react';
import AutoResizingTextArea from '../../../../components/AutoResizingTextArea';
import YesNoToggle from './YesNoToggle';
import {
  CYP_MEDICAL_SCREENING_INTRO,
  normalizeMedicalScreeningData,
} from '../config/cypAdhdMedicalScreeningConfig';

/** Card surface — the autism History section panel, shared with the CI page,
    the MSE panel and the structured risk panel. */
const PANEL_CLASS = 'bg-gray-50 rounded-lg border border-gray-300 text-left p-6 shadow-sm';

/** A row the clinician has not touched yet. */
const EMPTY_ROW = Object.freeze({ present: null, detail: '' });

export default function CypAdhdMedicalScreeningPanel({
  group,
  data,
  onChange,
  canEdit,
  title,
}) {
  const items = normalizeMedicalScreeningData(data).items;
  const heading = title || group.title;

  /**
   * Replace one row and emit the whole object. Always builds from the
   * `items` derived from the current props, so rapid clicks each start from
   * the latest render rather than from a stale closure.
   */
  const emitRow = (itemId, nextRow) => {
    if (!canEdit) return;
    onChange({
      ...normalizeMedicalScreeningData(data),
      items: { ...items, [itemId]: nextRow },
    });
  };

  const handlePresentChange = (itemId, clicked) => {
    if (!canEdit) return;
    const existing = items[itemId] || EMPTY_ROW;
    // Second click on the lit half clears the answer (adult 11_RSK.js rule).
    const nextPresent = existing.present === clicked ? null : clicked;
    emitRow(itemId, {
      present: nextPresent,
      // The detail belongs to a Yes. Leaving Yes drops it.
      detail: nextPresent === true ? (existing.detail || '') : '',
    });
  };

  const handleDetailChange = (itemId, value) => {
    if (!canEdit) return;
    const existing = items[itemId] || EMPTY_ROW;
    emitRow(itemId, { ...existing, detail: value });
  };

  /** One write that sets every row in this group to No with no detail. */
  const handleNoToAll = () => {
    if (!canEdit) return;
    const nextItems = { ...items };
    group.items.forEach((it) => {
      nextItems[it.id] = { present: false, detail: '' };
    });
    onChange({ ...normalizeMedicalScreeningData(data), items: nextItems });
  };

  // The bulk action is only offered where it saves clicks: a group with one
  // row is the same single click either way. Disabled once every row is
  // already No so it never looks available while doing nothing.
  const offerNoToAll = group.items.length > 1;
  const allAlreadyNo = group.items.every((it) => items[it.id]?.present === false);
  const noToAllDisabled = !canEdit || allAlreadyNo;

  const detailPlaceholder = (item) => (canEdit
    ? item.detailHint
    : 'Detail can only be added by the assigned clinician');

  return (
    <section className={PANEL_CLASS} data-testid={`medical-screening-${group.id}`}>
      {/* Header row mirrors the structured risk panel: title in the item
          column, "No to all?" centred over Present?, the details column
          left empty so the button lines up with the toggles below it. */}
      <div className="flex justify-between items-center mb-2">
        <div className="w-[30%] min-w-0">
          <h4 className="font-bold text-lg">{heading}</h4>
        </div>
        <div className="w-[20%] flex justify-center">
          {offerNoToAll && (
            <button
              type="button"
              onClick={handleNoToAll}
              aria-label={`No to all in ${group.title}`}
              className={`py-1.5 px-3 rounded-md shadow-sm font-semibold text-xs text-gray-900 bg-white border border-gray-400 transition-all duration-100 ${
                noToAllDisabled
                  ? 'opacity-60 cursor-not-allowed'
                  : 'hover:bg-red-700 hover:text-white hover:border-red-700'
              }`}
              disabled={noToAllDisabled}
            >
              No to all?
            </button>
          )}
        </div>
        <div className="w-[50%]" aria-hidden="true" />
      </div>

      {/* The orientation sentence sits on the Medical History group only:
          the single Family History row is self-explanatory, and repeating
          the paragraph would make the two cards look like two features. */}
      {group.id === 'medicalHistory' && (
        <p className="text-sm text-gray-600 mb-4">{CYP_MEDICAL_SCREENING_INTRO}</p>
      )}

      <div className="overflow-x-auto">
        <table className="w-full table-auto min-w-[640px]">
          <thead>
            <tr className="text-sm text-gray-600 border-t border-gray-200">
              <th className="text-left font-semibold py-2 w-[30%]">Item</th>
              <th className="text-center font-semibold py-2 w-[20%]">Present?</th>
              <th className="text-left font-semibold py-2 w-[50%]">Details</th>
            </tr>
          </thead>
          <tbody>
            {group.items.map((item) => {
              const row = items[item.id] || EMPTY_ROW;
              const isYes = row.present === true;
              return (
                <tr key={item.id} className="border-t border-gray-200">
                  <td className="py-3 pr-4 align-top">
                    <span className="text-sm font-semibold text-gray-800">{item.label}</span>
                  </td>
                  <td className="py-3 text-center align-top">
                    <YesNoToggle
                      value={row.present ?? null}
                      onChange={(present) => handlePresentChange(item.id, present)}
                      disabled={!canEdit}
                      categoryLabel={item.label}
                    />
                  </td>
                  <td className="py-3 align-top">
                    {/* The detail box exists only while the row is Yes. A No
                        row has nothing to add (the clinician notes box at the
                        foot of the section is there for anything else). */}
                    {isYes && (
                      <AutoResizingTextArea
                        aria-label={`${item.label} detail`}
                        value={row.detail || ''}
                        onChange={(e) => handleDetailChange(item.id, e.target.value)}
                        placeholder={detailPlaceholder(item)}
                        readOnly={!canEdit}
                        // The shared textarea defaults to half width with a
                        // left margin (its PAD layout); here it fills the cell.
                        className="!w-full !ml-0 text-sm"
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
